import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import throttle from 'lodash/throttle';

import { ROWS_PER_PAGE_OPTIONS } from 'consts';
import { IPageResult } from 'types';

import { PaginationState, UsePaginationConfig, UsePaginationReturn } from './types';
import { getStoredPagination } from './helpers';

/**
 * Unified pagination hook for managing table pagination state.
 *
 * Features:
 * - localStorage persistence (throttled to avoid excessive writes)
 * - API response synchronization
 * - Ready-to-use query params for API calls
 * - Callback support for triggering side effects
 * - Utility functions for common operations
 */
export const usePagination = ({
  localStorageKey,
  initialPage = 0,
  initialSize = 50,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  onPaginationChange,
}: UsePaginationConfig): UsePaginationReturn => {
  // Track if this is the initial mount to avoid calling onPaginationChange
  const isInitialMount = useRef(true);

  // Store the callback in a ref to avoid stale closures
  const onPaginationChangeRef = useRef(onPaginationChange);
  useEffect(() => {
    onPaginationChangeRef.current = onPaginationChange;
  }, [onPaginationChange]);


  const [pagination, setPagination] = useState<PaginationState>(() =>
    getStoredPagination(localStorageKey, initialPage, initialSize)
  );

  // Only create localStorage save function if key is provided
  const saveToLocalStorage = useMemo(
    () => {
      if (!localStorageKey) return null;

      return throttle((paginationData: PaginationState) => {
        try {
          localStorage.setItem(localStorageKey, JSON.stringify(paginationData));
        } catch (e) {
          console.error(`[usePagination] Failed to save to localStorage for key "${localStorageKey}":`, e);
        }
      }, 1000);
    },
    [localStorageKey]
  );

  // Save to localStorage when pagination changes (only if persistence is enabled)
  useEffect(() => {
    if (saveToLocalStorage) {
      saveToLocalStorage(pagination);
    }
  }, [pagination, saveToLocalStorage]);

  // Cleanup throttle on unmount
  useEffect(() => () => {
    if (saveToLocalStorage) {
      saveToLocalStorage.cancel();
    }
  }, [saveToLocalStorage]);

  const paginatedQuery = useMemo(
    () => ({ page: pagination.page, size: pagination.size }),
    [pagination.page, pagination.size]
  );

  const uiPage = pagination.page + 1;
  const hasNextPage = pagination.page < pagination.totalPages - 1;
  const hasPreviousPage = pagination.page > 0;


  const onPageChange = useCallback((uiPageNumber: number) => {
    if (uiPageNumber < 1) return;

    const newPage = uiPageNumber - 1;

    setPagination((prev) => {
      if (prev.page === newPage) return prev;
      return { ...prev, page: newPage };
    });

    // Trigger callback with new query
    if (onPaginationChangeRef.current) {
      onPaginationChangeRef.current({
        page: newPage,
        size: pagination.size
      });
    }
  }, [pagination.size]);

  const onSizeChange = useCallback((newSize: number) => {
    if (newSize <= 0) return;

    setPagination((prev) => {
      if (prev.size === newSize && prev.page === 0) return prev;
      return { ...prev, size: newSize, page: 0 };
    });

    // Trigger callback with new query (page reset to 0)
    if (onPaginationChangeRef.current) {
      onPaginationChangeRef.current({ page: 0, size: newSize });
    }
  }, []);

  const updateFromApiResponse = useCallback((pageResult?: IPageResult) => {
    if (!pageResult) return;

    const { totalElements, totalPages, page, size } = pageResult;

    if (typeof totalElements !== 'number' || typeof totalPages !== 'number') {
      return;
    }

    setPagination((prev) => {
      // Only update if values changed to avoid unnecessary re-renders
      const hasChanges =
        prev.totalRows !== totalElements ||
        prev.totalPages !== totalPages ||
        prev.page !== page ||
        prev.size !== size;

      if (!hasChanges) return prev;

      return {
        ...prev,
        page,
        size,
        totalRows: totalElements,
        totalPages,
      };
    });
  }, []);

  const resetPage = useCallback(() => {
    setPagination((prev) => {
      if (prev.page === 0) return prev;
      return { ...prev, page: 0 };
    });
  }, []);

  const resetPageAndFetch = useCallback(() => {
    setPagination((prev) => {
      if (prev.page === 0) return prev;
      return { ...prev, page: 0 };
    });

    if (onPaginationChangeRef.current) {
      onPaginationChangeRef.current({ page: 0, size: pagination.size });
    }
  }, [pagination.size]);

  // Skip initial mount callback
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, []);

  return {
    pagination,
    paginatedQuery,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    resetPage,
    resetPageAndFetch,
    uiPage,
    rowsPerPageOptions,
    hasNextPage,
    hasPreviousPage,
  };
};
